import React, { FC, useRef, useState } from 'react';
import { myInfoDataStore } from 'src/store/myInfo';
import { salaryDataStore } from 'src/store/mySalaryInfo';
import { preworkDataStore } from 'src/store/preworkInfo';
import { getDateMonth, getDateYear, getDateFormat } from 'src/utils/dateSet';
import { getMemberInfo } from 'src/utils/common';
import DescLabel from 'src/components/atoms/label/DescLabel';
import './style.scss';

interface Props {
  data?: any;
}

const WorkingPeriod: FC<Props> = ({ data }) => {
  const myInfoDataStateResult = myInfoDataStore((state: any) => state.result).data || {};
  const salaryInfoDataStateResult = salaryDataStore((state: any) => state.result).data || {};
  const preworkInfoDataStateResult = preworkDataStore((state: any) => state.result).data || [];
  const workData: any = [];

  // 현 근무지
  const currentSMonth = getDateMonth(myInfoDataStateResult.start_date, 'YYYYMMDD');
  const currentEMonth = getDateMonth(myInfoDataStateResult.end_date, 'YYYYMMDD');
  const sYear = getDateYear(myInfoDataStateResult.start_date, 'YYYYMMDD');
  const eYear = getDateYear(myInfoDataStateResult.end_date, 'YYYYMMDD');

  workData.push({
    idx: preworkInfoDataStateResult.length,
    company: salaryInfoDataStateResult.co_cd,
    sMonth: currentSMonth,
    eMonth: currentEMonth,
    sYear,
    eYear,
    income: salaryInfoDataStateResult.tsal_base || 0,
    businessNum: salaryInfoDataStateResult.co_cd,
    workStDt: getDateFormat(myInfoDataStateResult.start_date, 'YYYYMMDD'),
    workEdDt: getDateFormat(myInfoDataStateResult.end_date, 'YYYYMMDD'),
    type: 'before',
  });

  // 이전 근무지
  preworkInfoDataStateResult.forEach((item: any, idx: number) => {
    const sMonth = getDateMonth(item.start_date, 'YYYYMMDD');
    const eMonth = getDateMonth(item.end_date, 'YYYYMMDD');
    const sYear = getDateYear(item.start_date, 'YYYYMMDD');
    const eYear = getDateYear(item.end_date, 'YYYYMMDD');
    workData.push({
      idx: idx,
      company: item.prev_co_name,
      sMonth,
      eMonth,
      sYear,
      eYear,
      income: item.tsal_base || 0,
      businessNum: item.prev_co_num,
      workStDt: getDateFormat(item.start_date, 'YYYYMMDD'),
      workEdDt: getDateFormat(item.end_date, 'YYYYMMDD'),
      type: 'before',
    });
  });

  const colorRange = ['#FF9345', '#666'];

  // 귀속연도(acct_year) 기준으로만 근로기간을 표기
  const memberInfo: any = getMemberInfo() || {};
  const acctYear = parseInt(memberInfo.acct_year, 10) || new Date().getFullYear();

  // 종료일이 없는(재직 중인) 근무는 귀속연도 12월까지 근무 중인 것으로 처리
  workData.forEach((item: any) => {
    if (!Number.isFinite(item.eYear) || !Number.isFinite(item.eMonth)) {
      item.eYear = acctYear;
      item.eMonth = 12;
    }
  });

  // 유효한(연/월이 모두 숫자인) 근무 항목인지 판단
  const isValidPeriod = (item: any) =>
    Number.isFinite(item.sYear) &&
    Number.isFinite(item.sMonth) &&
    Number.isFinite(item.eYear) &&
    Number.isFinite(item.eMonth);

  // 그리드는 귀속연도 1월부터 12월까지만 표기
  const timeline: { year: number; month: number }[] = Array.from({ length: 12 }, (_, idx) => ({
    year: acctYear,
    month: idx + 1,
  }));

  const yearGroups: { year: number; count: number }[] = [{ year: acctYear, count: 12 }];

  // 컬럼이 항상 귀속연도 12개월 고정이므로, 가용 폭을 꽉 채우도록 %로 분배
  const colWidthPercent = 100 / timeline.length;

  const monthView = (item: any) => {
    const bgColor = colorRange[item.idx] || colorRange[colorRange.length - 1];

    if (!isValidPeriod(item)) {
      return <div className="_date_div" style={{ width: '100%' }} />;
    }

    // 귀속연도(acct_year) 범위 밖의 근로기간은 해당 연도 범위로 잘라서 표기
    const sKey = item.sYear * 100 + item.sMonth;
    const eKey = item.eYear * 100 + item.eMonth;
    const yearStartKey = acctYear * 100 + 1;
    const yearEndKey = acctYear * 100 + 12;

    if (eKey < yearStartKey || sKey > yearEndKey) {
      return <div className="_date_div" style={{ width: '100%' }} />;
    }

    const clippedSKey = Math.max(sKey, yearStartKey);
    const clippedEKey = Math.min(eKey, yearEndKey);

    const startIdx = timeline.findIndex(
      t => t.year === Math.floor(clippedSKey / 100) && t.month === clippedSKey % 100
    );
    const endIdx = timeline.findIndex(
      t => t.year === Math.floor(clippedEKey / 100) && t.month === clippedEKey % 100
    );

    return (
      <div className="_date_div" style={{ width: '100%' }}>
        {timeline.map((t: any, idx: number) => {
          let barStatus = '_normal';
          let checkBarStatus = '_normal';

          if (idx === startIdx - 1) barStatus = '_chk';
          if (idx === endIdx) barStatus = '_chk';

          if (idx === startIdx) checkBarStatus = '_start';
          if (idx === endIdx) checkBarStatus = '_end';
          if (idx === startIdx && idx === endIdx) checkBarStatus = '_start_end';

          return (
            <div
              className={['_item _col_item', barStatus].join(' ')}
              style={{ width: `${colWidthPercent}%` }}
              key={idx}
            >
              <div className="_bg_bar " />
              {idx >= startIdx && idx <= endIdx ? (
                <div
                  className={['_check_bar', checkBarStatus].join(' ')}
                  style={{ backgroundColor: bgColor }}
                />
              ) : (
                <div className="_blank_bar " />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const drawChart = () => {
    return (
      <div className="_cont_bg">
        <div className="_body_row">
          <div className="_label_col">
            <div className="_label_cell _header">
              <div className="_label"> 근무 개월</div>
            </div>
            {workData.map((item: any, idx: number) => {
              return (
                <div className="_label_cell" key={idx}>
                  <div className="_main_cont">{item.company}</div>
                </div>
              );
            })}
          </div>
          <div className="_scroll_col">
            <div className="_scroll_inner" style={{ width: '100%' }}>
              <div className="_month_header">
                <div className="_year_row">
                  {yearGroups.map((group: any, idx: number) => {
                    return (
                      <div
                        className="_year_item"
                        style={{ width: `${group.count * colWidthPercent}%` }}
                        key={idx}
                      >
                        <div className="_label">{group.year}년</div>
                      </div>
                    );
                  })}
                </div>
                <div className="_month_row">
                  {timeline.map((t: any, idx: number) => {
                    return (
                      <div className="_col_item" style={{ width: `${colWidthPercent}%` }} key={idx}>
                        <div className="_label">{t.month}월</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {workData.map((item: any, idx: number) => {
                return (
                  <div className="_bar_row" key={idx}>
                    {monthView(item)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="_step_b_working_period">
      <div className="_desc_div">
        <div className="_item">
          연말정산간소화 서비스에서 <em>"조회한 항목 한번에 다운로드하기"</em> 를 이용해서
          다운로드한 문서를 업로드 하여야 합니다.
        </div>
        <div className="_item">
          <em>근무개월수(종•전 근무지 포함)가 12개월이 아닌 사용자</em>의 경우 상세 항목을 반드시
          업로드 하여야 합니다.
        </div>
      </div>
      <div className="_content">{drawChart()}</div>
    </div>
  );
};

export default WorkingPeriod;
